import type { ClientEnvUAFlags } from "./types";

const isTruthy = (v: unknown) => !!v;

export const isOfficialApkUserAgent = (uaRaw: string): boolean =>
  /\b9BETIN\//i.test(uaRaw || "");

export const parseUAFlags = (uaRaw: string): ClientEnvUAFlags => {
  const ua = uaRaw || "";
  const ual = ua.toLowerCase();

  const isAndroid = /android/i.test(ual);
  const isIOS = /(iphone|ipad|ipod)/i.test(ual);
  const isMobile = isAndroid || isIOS || /mobile/i.test(ual);

  // Facebook family in-app browsers typically include these tokens.
  // Examples seen in the wild:
  // - FBAN/FB4A; FBAV/...
  // - FBAN/FBIOS; FBAV/...
  // - [FB_IAB/FB4A;FBAV/...]
  // Be intentionally a bit lenient: different builds / regions may use slightly different tokens.
  const facebookTokens = [
    "fb_iab", // Facebook In-App Browser marker
    "fban", // Facebook App name
    "fbav", // Facebook App version
    "fbfor", // Facebook for Android/iOS
    "fb4a", // Facebook for Android
    "fbios", // Facebook for iOS
    "fblite", // Facebook Lite
  ];
  const isFacebook = facebookTokens.some((t) => ual.includes(t));
  const isFacebookIAB = ual.includes("fb_iab") || ual.includes("fban/fb4a") || ual.includes("fban/fbios");

  const isInstagram = ual.includes("instagram");

  // Messenger variants:
  // - FBAN/Messenger
  // - fb_messenger
  // - "Messenger" token sometimes appears alongside FBAN/FBAV
  const isMessenger =
    ual.includes("fban/messenger") ||
    ual.includes("fb_messenger") ||
    (ual.includes("messenger") && isFacebook);

  // WebView heuristics (not perfect, but useful as a fallback bucket)
  const isAndroidWebView =
    isAndroid &&
    isTruthy(
      /\bwv\b/i.test(ua) ||
        /; wv\)/i.test(ua) ||
        (/version\/\d+\.\d+/i.test(ual) && /chrome\//i.test(ual) && !/safari\//i.test(ual)) ||
        /webview/i.test(ual)
    );

  // iOS WebView UAs usually miss the "Safari" token.
  // Most iOS browsers (Chrome, Edge, Firefox) still include "Safari/..." for compatibility.
  const isIOSWebView =
    isIOS && !/safari/i.test(ual) && !/crios|fxios|edgios|opios/i.test(ual);

  return {
    ua,
    isAndroid,
    isIOS,
    isMobile,
    isFacebook,
    isFacebookIAB,
    isInstagram,
    isMessenger,
    isAndroidWebView,
    isIOSWebView,
  };
};
